import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    model,
    signal,
    untracked
} from "@angular/core";
import {
    LgLocalizationModule,
    LgTranslateService,
    useTranslationNamespace
} from "@logex/framework/lg-localization";
import { CdkDragDrop, CdkDrag, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { LgIconComponent } from "../lg-icon/lg-icon.component";
import { LgSimpleTooltipDirective } from "../../lg-tooltip/lg-simple-tooltip.directive";

import { LgOrderedItemMenuComponent } from "./lg-ordered-item-menu/lg-ordered-item-menu.component";
import { LgOrderedItemComponent } from "./lg-ordered-item/lg-ordered-item.component";
import { getErrorState } from "./utils/utils";
import {
    OrderedItemMenuType,
    type OrderedItemErrorState,
    type OrderedItemActiveMenu,
    type OrderedItemId,
    type OrderedItem
} from "./lg-ordered-item-selector.model";

const INIT_ACTIVE_MENU = {
    [OrderedItemMenuType.ReplaceItemMenu]: undefined,
    [OrderedItemMenuType.AddItemMenu]: false
} as const satisfies OrderedItemActiveMenu;

@Component({
    selector: "lg-ordered-item-selector",
    templateUrl: "./lg-ordered-item-selector.component.html",
    styleUrls: ["./lg-ordered-item-selector.component.scss"],
    imports: [
        LgIconComponent,
        LgSimpleTooltipDirective,
        LgOrderedItemMenuComponent,
        LgOrderedItemComponent,
        LgLocalizationModule,
        CdkDropList,
        CdkDrag
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [useTranslationNamespace("FW._Directives._OrderedItemSelector")]
})
export class LgOrderedItemSelectorComponent {
    selectedItems = model.required<OrderedItemId[]>();
    readonly menuItems = input.required<OrderedItem[]>();
    readonly isDisabled = input<boolean>(false);
    readonly minItems = input<number>(0);
    readonly maxItems = input<number>(Infinity);

    private _elementRef = inject(ElementRef);
    private _lgTranslate = inject(LgTranslateService);

    protected readonly _menuType = OrderedItemMenuType;

    protected _activeMenu = signal<OrderedItemActiveMenu>(INIT_ACTIVE_MENU);

    protected readonly _filteredMenuItems = computed<OrderedItem[]>(() => {
        const menuItems = this.menuItems();
        const selectedItems = this.selectedItems();

        return menuItems.filter(menuItem => !selectedItems.includes(menuItem.id));
    });

    protected readonly _resolvedError = computed<OrderedItemErrorState | null>(() => {
        return getErrorState(
            this.menuItems().length,
            this.selectedItems().length,
            this.minItems(),
            this.maxItems(),
            this._lgTranslate
        );
    });

    protected readonly _menuItemNamesDict = computed<Record<OrderedItemId, OrderedItem>>(() => {
        return this.menuItems().reduce(
            (acc, item) => {
                acc[item.id] = item;

                return acc;
            },
            {} as Record<OrderedItemId, OrderedItem>
        );
    });

    constructor() {
        // Close menus when state changes externally
        effect((): void => {
            const selectedItems = this.selectedItems();
            const hasError = this._resolvedError();

            // Close add menu when there's an error
            if (hasError) {
                this._activeMenu.update(currentState => ({
                    ...currentState,
                    [OrderedItemMenuType.AddItemMenu]: false
                }));
            }

            // Close replace item menu if the item is no longer selected
            const activeReplaceMenu = untracked(
                () => this._activeMenu()[OrderedItemMenuType.ReplaceItemMenu]
            );
            if (activeReplaceMenu !== undefined && !selectedItems.includes(activeReplaceMenu)) {
                this._activeMenu.update(currentState => ({
                    ...currentState,
                    [OrderedItemMenuType.ReplaceItemMenu]: undefined
                }));
            }
        });
    }

    protected _toggleMenu(menuType: OrderedItemMenuType, activeMenuId?: OrderedItemId): void {
        if (this.isDisabled()) {
            return;
        }

        this._activeMenu.update(currentState => {
            const isMenuActive = currentState[menuType];

            // If any menu is active, close all menus
            if (Object.values(currentState).some(value => value)) {
                return this._getCloseMenusState();
            }

            // Toggle the state of the specified menu
            return {
                ...currentState,
                [menuType]:
                    menuType === OrderedItemMenuType.ReplaceItemMenu
                        ? isMenuActive === activeMenuId
                            ? undefined
                            : activeMenuId
                        : !isMenuActive
            };
        });
    }

    protected _handleButtonMenuItemClick(
        menuOption: OrderedItem,
        selectedHierarchy: OrderedItemId
    ): void {
        this.selectedItems.update(items => {
            const updatedItems = [...items];
            const selectedIndex = updatedItems.findIndex(
                updatedItem => updatedItem === selectedHierarchy
            );
            const menuOptionIndex = updatedItems.findIndex(
                updatedItem => updatedItem === menuOption.id
            );

            if (menuOptionIndex !== -1 && selectedIndex !== -1) {
                // Switch items if both exist
                [updatedItems[menuOptionIndex], updatedItems[selectedIndex]] = [
                    selectedHierarchy,
                    menuOption.id
                ];
            } else if (selectedIndex !== -1) {
                // Replace selected item with menu option
                updatedItems[selectedIndex] = menuOption.id;
            }

            return updatedItems;
        });

        this._toggleMenu(OrderedItemMenuType.ReplaceItemMenu);
    }

    protected _handleAddItemMenuClick(itemId: OrderedItemId): void {
        const selectedItem = this.menuItems().find(item => item.id === itemId);

        if (!selectedItem) {
            return;
        }

        this.selectedItems.update(currentItems => [...currentItems, selectedItem.id]);
        this._toggleMenu(OrderedItemMenuType.AddItemMenu);
    }

    protected _removeItem(itemId: OrderedItemId): void {
        this.selectedItems.update(items => items.filter(item => item !== itemId));
    }

    protected _onDrop(event: CdkDragDrop<OrderedItemId[]>): void {
        if (this.isDisabled()) {
            return;
        }

        this.selectedItems.update(items => {
            const reorderedItems = [...items];
            moveItemInArray(reorderedItems, event.previousIndex, event.currentIndex);
            return reorderedItems;
        });
    }

    @HostListener("document:click", ["$event"])
    protected _handleOutsideClick(event: MouseEvent): void {
        if (!this._elementRef.nativeElement.contains(event.target)) {
            this._activeMenu.set(this._getCloseMenusState());
        }
    }

    private _getCloseMenusState(): OrderedItemActiveMenu {
        return {
            [OrderedItemMenuType.ReplaceItemMenu]: undefined,
            [OrderedItemMenuType.AddItemMenu]: false
        };
    }
}
